/**
 * Y GO Shuttle Services France - Layout Components
 * Composants Header et Footer réutilisables avec i18n
 */
import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Menu, 
  X,
  User,
  LogOut,
  Calendar,
  ChevronDown,
  Home,
  ExternalLink,
  Search,
  Car
} from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../context/AuthContext';
import { useBranding } from '../../context/BrandingContext';
import LanguageSelector from '../LanguageSelector';
import SafeImage from '../SafeImage';
import config from 'config';

const API_URL = config.API_URL;

// Navigation items with translation keys
const getNavItems = (t) => [
  { label: t('nav.home'), path: '/' },
  { label: t('nav.services'), path: '/prestations' },
  { label: t('nav.transfers'), path: '/transferts' },
  { label: t('nav.cruises'), path: '/croisieres-excursions' },
  { label: t('nav.business'), path: '/entreprises' },
  { label: t('nav.fleet'), path: '/flotte' },
  { label: t('nav.quoteContact'), path: '/contact' },
];

// Legal links with translation keys
const getLegalLinks = (t) => [
  { label: t('footer.legalMentions'), path: '/legal/mentions' },
  { label: t('footer.terms'), path: '/legal/cgv' },
  { label: t('footer.privacy'), path: '/legal/confidentialite' },
  { label: t('footer.cookies'), path: '/legal/cookies' },
  { label: 'shore.y-go.fr', path: 'https://shore.y-go.fr', external: true },
];

// User Menu Component
const UserMenu = ({ user, onLogout }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Check if user has a specific role (handles both role string and roles array)
  const hasRole = (roleName) => {
    if (user?.role === roleName) return true;
    if (user?.roles) {
      return user.roles.some(r => r.name === roleName || r === roleName);
    }
    return false;
  };
  
  const getDashboardPath = () => {
    if (hasRole('SUPERADMIN')) return '/admin';
    if (hasRole('CHAUFFEUR')) return '/chauffeur';
    return '/client';
  };
  
  const getRoleLabel = () => {
    if (hasRole('SUPERADMIN')) return t('roles.superadmin');
    if (hasRole('CHAUFFEUR')) return t('roles.chauffeur');
    return t('roles.client');
  };
  
  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 text-[#E5E5E5] hover:text-[#C9A24D] transition-colors"
      >
        <div className="w-8 h-8 bg-[#C9A24D]/20 flex items-center justify-center rounded-full">
          <User className="w-4 h-4 text-[#C9A24D]" />
        </div>
        <span className="hidden sm:inline text-sm font-medium">{t('common.myAccount')}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-56 bg-[#1F1F1F] border border-[#C9A24D]/20 shadow-xl z-50">
          <div className="px-4 py-3 border-b border-[#C9A24D]/10">
            <p className="text-[#E5E5E5] font-medium text-sm truncate">{user?.first_name} {user?.last_name}</p>
            <p className="text-[#E5E5E5]/60 text-xs">{getRoleLabel()}</p>
          </div>
          
          <div className="py-2">
            <Link
              to="/"
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-3 px-4 py-2 text-[#E5E5E5]/80 hover:text-[#C9A24D] hover:bg-[#C9A24D]/5 transition-colors text-sm"
            >
              <Home className="w-4 h-4" />
              {t('nav.home')}
            </Link>
            
            <Link
              to={getDashboardPath()}
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-3 px-4 py-2 text-[#E5E5E5]/80 hover:text-[#C9A24D] hover:bg-[#C9A24D]/5 transition-colors text-sm"
            >
              <User className="w-4 h-4" />
              {t('common.mySpace')}
            </Link>
            
            {user?.role === 'CLIENT' && (
              <Link
                to="/client"
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-3 px-4 py-2 text-[#E5E5E5]/80 hover:text-[#C9A24D] hover:bg-[#C9A24D]/5 transition-colors text-sm"
              >
                <Calendar className="w-4 h-4" />
                {t('common.myBookings')}
              </Link>
            )}
          </div>
          
          <div className="border-t border-[#C9A24D]/10 py-2">
            <button
              onClick={() => {
                setIsOpen(false);
                onLogout();
              }}
              className="flex items-center gap-3 px-4 py-2 text-red-400 hover:text-red-300 hover:bg-red-500/5 transition-colors text-sm w-full"
            >
              <LogOut className="w-4 h-4" />
              {t('common.logout')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Mobile Menu Component
const MobileMenu = ({ isOpen, onClose, user, onLogout }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const NAV_ITEMS = getNavItems(t);
  
  if (!isOpen) return null;
  
  // Check if user has a specific role (handles both role string and roles array)
  const hasRole = (roleName) => {
    if (user?.role === roleName) return true;
    if (user?.roles) {
      return user.roles.some(r => r.name === roleName || r === roleName);
    }
    return false;
  };
  
  const getDashboardPath = () => {
    if (hasRole('SUPERADMIN')) return '/admin';
    if (hasRole('CHAUFFEUR')) return '/chauffeur';
    return '/client';
  };
  
  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      <div className="absolute top-0 right-0 w-80 max-w-full h-full bg-[#0B0B0B] border-l border-[#C9A24D]/20 overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-[#C9A24D]/20">
          <span className="font-['Playfair_Display'] text-xl font-bold text-[#C9A24D]">Y GO</span>
          <button onClick={onClose} className="text-[#E5E5E5]/60 hover:text-[#C9A24D]">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <nav className="p-4 space-y-1">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={onClose}
              className={`block px-4 py-3 text-sm font-medium transition-colors ${
                location.pathname === item.path 
                  ? 'text-[#C9A24D] bg-[#C9A24D]/10' 
                  : 'text-[#E5E5E5]/80 hover:text-[#C9A24D] hover:bg-[#C9A24D]/5'
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>
        
        <div className="border-t border-[#C9A24D]/20 p-4 space-y-3">
          {user ? (
            <>
              <div className="px-4 py-3 bg-[#1F1F1F] mb-3">
                <p className="text-[#E5E5E5] font-medium text-sm">{user?.first_name} {user?.last_name}</p>
                <p className="text-[#E5E5E5]/60 text-xs">{user?.email}</p>
              </div>
              
              <Link
                to={getDashboardPath()}
                onClick={onClose}
                className="flex items-center gap-3 px-4 py-3 text-[#E5E5E5]/80 hover:text-[#C9A24D] transition-colors"
              >
                <User className="w-4 h-4" />
                {t('common.mySpace')}
              </Link>
              
              <button
                onClick={() => { onClose(); onLogout(); }}
                className="flex items-center gap-3 px-4 py-3 text-red-400 hover:text-red-300 w-full"
              >
                <LogOut className="w-4 h-4" />
                {t('common.logout')}
              </button>
            </>
          ) : (
            <Link to="/login" onClick={onClose}>
              <Button variant="outline" className="w-full border-[#C9A24D] text-[#C9A24D] rounded-none">
                {t('common.login')}
              </Button>
            </Link>
          )}
          
          <Link to="/booking" onClick={onClose}>
            <Button className="w-full bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold rounded-none">
              {t('common.book')}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

// Main Header Component
export const Header = ({ activePage }) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { logoUrl, siteTitle } = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const NAV_ITEMS = getNavItems(t);
  
  const handleLogout = () => {
    logout();
    navigate('/');
  };
  
  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    if (path.startsWith('/#')) return location.pathname === '/' && location.hash === path.slice(1);
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };
  
  return (
    <>
      <header 
        data-testid="main-header"
        className="fixed top-0 left-0 right-0 z-50 bg-[#0B0B0B]/95 backdrop-blur-sm border-b border-[#C9A24D]/20"
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <SafeImage
                src={logoUrl}
                fallbackSrc="/ygo-logo.png"
                alt={siteTitle || 'Y GO'}
                className="h-10 sm:h-12 w-auto"
                placeholderClassName="h-10 sm:h-12 w-24"
              />
            </Link>
            
            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-6">
              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`text-sm font-medium transition-colors ${
                    isActive(item.path) || activePage === item.label
                      ? 'text-[#C9A24D]' 
                      : 'text-[#E5E5E5]/80 hover:text-[#C9A24D]'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
            
            {/* Right Side Actions */}
            <div className="flex items-center gap-3 sm:gap-4">
              {/* Language Selector - Always visible */}
              <LanguageSelector variant="minimal" />
              
              {/* Phone - Hidden on small screens */}
              <a href="tel:+33278780404" className="hidden xl:flex items-center gap-2 text-[#C9A24D] text-sm font-medium">
                <Phone className="w-4 h-4" />
                <span>+33 2 78 78 04 04</span>
              </a>
              
              {/* Auth Section */}
              <div className="hidden sm:flex items-center gap-3">
                {user ? (
                  <UserMenu user={user} onLogout={handleLogout} />
                ) : (
                  <Link to="/login">
                    <Button 
                      variant="outline"
                      className="border-[#C9A24D]/50 text-[#C9A24D] hover:bg-[#C9A24D]/10 rounded-none text-sm"
                    >
                      {t('common.login')}
                    </Button>
                  </Link>
                )}
              </div>
              
              {/* Booking Button */}
              <Link to="/booking">
                <Button 
                  data-testid="header-cta-booking"
                  className="bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold px-4 sm:px-6 py-2 text-sm rounded-none"
                >
                  {t('common.book')}
                </Button>
              </Link>
              
              {/* Mobile Menu Button */}
              <button 
                onClick={() => setMobileMenuOpen(true)}
                className="lg:hidden text-[#E5E5E5] hover:text-[#C9A24D] p-2"
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </header>
      
      {/* Mobile Menu */}
      <MobileMenu 
        isOpen={mobileMenuOpen} 
        onClose={() => setMobileMenuOpen(false)}
        user={user}
        onLogout={handleLogout}
      />
    </>
  );
};

// Main Footer Component
export const Footer = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const { logoUrl, siteTitle, siteTagline } = useBranding();
  const [siteContact, setSiteContact] = useState({ siret: '' });
  const [companyInfo, setCompanyInfo] = useState(null);
  
  // Fetch site contact info and company info
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [contactRes, companyRes] = await Promise.all([
          fetch(`${API_URL}/api/settings/public/contact`),
          fetch(`${API_URL}/api/settings/public/company`)
        ]);
        if (contactRes.ok) {
          setSiteContact(await contactRes.json());
        }
        if (companyRes.ok) {
          const data = await companyRes.json();
          if (data && Object.keys(data).length > 0) {
            setCompanyInfo(data);
          }
        }
      } catch (err) {
        console.warn('Failed to fetch footer data:', err);
      }
    };
    fetchData();
  }, []);
  
  // Derived contact info: prefer company_info, fallback to siteContact, then hardcoded
  const rawPhone = companyInfo?.phone || siteContact?.phone || '+33 2 78 78 04 04';
  // Format phone for display: +33278780404 → +33 2 78 78 04 04
  const displayPhone = rawPhone.includes(' ') ? rawPhone : rawPhone.replace(/^\+(\d{2})(\d)(\d{2})(\d{2})(\d{2})(\d{2})$/, '+$1 $2 $3 $4 $5 $6');
  const displayEmail = companyInfo?.email || siteContact?.email || 'booking@y-go.fr';
  const displaySiret = companyInfo?.siret || siteContact?.siret || '';
  const displayAddress = companyInfo?.address
    ? `${companyInfo.address}${companyInfo.address_complement ? ', ' + companyInfo.address_complement : ''}`
    : (siteContact?.address_line1 || '51 quai Southampton');
  const displayCityLine = companyInfo?.postal_code
    ? `${companyInfo.postal_code} ${companyInfo.city || ''}`
    : (siteContact?.address_line2 || '76600 Le Havre');
  const displayHours = siteContact?.support_hours || '9h-19h';
  
  const NAV_ITEMS = getNavItems(t);
  const LEGAL_LINKS = getLegalLinks(t);
  
  return (
    <footer data-testid="main-footer" className="bg-[#0B0B0B] border-t border-[#C9A24D]/20">
      {/* Main Footer */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          
          {/* Bloc Société */}
          <div>
            <Link to="/" className="inline-block mb-4">
              <SafeImage
                src={logoUrl}
                fallbackSrc="/ygo-logo.png"
                alt={siteTitle || 'Y GO'}
                className="h-10 w-auto"
                placeholderClassName="h-10 w-20"
              />
            </Link>
            <p className="text-[#E5E5E5]/60 text-sm mb-2">{siteTagline || t('footer.tagline')}</p>
            <address className="text-[#E5E5E5]/70 text-sm not-italic leading-relaxed">
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-[#C9A24D] flex-shrink-0 mt-0.5" />
                <div>
                  {displayAddress}<br />
                  {displayCityLine}
                </div>
              </div>
            </address>
          </div>
          
          {/* Bloc Navigation */}
          <div>
            <h3 className="text-[#E5E5E5] font-semibold text-sm uppercase tracking-wider mb-4">
              {t('footer.navigation')}
            </h3>
            <nav className="space-y-2">
              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className="block text-[#E5E5E5]/70 hover:text-[#C9A24D] text-sm transition-colors"
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>
          
          {/* Bloc Liens Utiles */}
          <div>
            <h3 className="text-[#E5E5E5] font-semibold text-sm uppercase tracking-wider mb-4">
              {t('footer.usefulLinks')}
            </h3>
            <nav className="space-y-2">
              {LEGAL_LINKS.map((item) => (
                item.external ? (
                  <a
                    key={item.path}
                    href={item.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-[#E5E5E5]/70 hover:text-[#C9A24D] text-sm transition-colors"
                  >
                    {item.label}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                ) : (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="block text-[#E5E5E5]/70 hover:text-[#C9A24D] text-sm transition-colors"
                  >
                    {item.label}
                  </Link>
                )
              ))}
            </nav>
          </div>
          
          {/* Bloc Contact */}
          <div>
            <h3 className="text-[#E5E5E5] font-semibold text-sm uppercase tracking-wider mb-4">
              {t('footer.contactUs')}
            </h3>
            <div className="space-y-3">
              <a 
                href={`tel:${displayPhone.replace(/\s/g, '')}`} 
                className="flex items-center gap-2 text-[#E5E5E5]/70 hover:text-[#C9A24D] text-sm transition-colors"
              >
                <Phone className="w-4 h-4 text-[#C9A24D]" />
                {displayPhone}
              </a>
              <a 
                href={`mailto:${displayEmail}`} 
                className="flex items-center gap-2 text-[#E5E5E5]/70 hover:text-[#C9A24D] text-sm transition-colors"
              >
                <Mail className="w-4 h-4 text-[#C9A24D]" />
                {displayEmail}
              </a>
              <div className="flex items-center gap-2 text-[#E5E5E5]/70 text-sm">
                <Clock className="w-4 h-4 text-[#C9A24D]" />
                <div>
                  <span>{t('common.support')} {displayHours}</span>
                  <span className="text-[#C9A24D] ml-1">| {t('common.service247')}</span>
                </div>
              </div>
            </div>
            
            {/* CTA */}
            <div className="mt-6 space-y-2">
              <Link to="/booking">
                <Button className="w-full bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold rounded-none text-sm">
                  {t('common.bookNow')}
                </Button>
              </Link>
              <Link to="/suivi">
                <Button variant="outline" className="w-full border-[#C9A24D] text-[#C9A24D] hover:bg-[#C9A24D]/10 rounded-none text-sm">
                  <Search className="w-4 h-4 mr-2" />
                  Suivre ma réservation
                </Button>
              </Link>
              <Link to="/register/pro" data-testid="footer-partner-link">
                <Button variant="outline" className="w-full border-[#E5E5E5]/20 text-[#E5E5E5]/70 hover:border-[#C9A24D] hover:text-[#C9A24D] hover:bg-[#C9A24D]/5 rounded-none text-sm">
                  <Car className="w-4 h-4 mr-2" />
                  Espace partenaires
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
      
      {/* Copyright Bar */}
      <div className="border-t border-[#C9A24D]/10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-2 text-xs text-[#E5E5E5]/40">
            <p>© {currentYear} {t('footer.company')}. {t('common.allRightsReserved')}.</p>
            {displaySiret && (
              <p>VTC immatriculé - SIRET {displaySiret}</p>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default { Header, Footer };
